import React, {useEffect, useState, useCallback} from 'react';
import {
  View,
  FlatList,
  Text,
  Image,
  TouchableOpacity,
  Alert,
  Modal,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import {useFocusEffect, useNavigation} from '@react-navigation/native';
import {Colors} from '../../constants/Colors';
import FontAwesome from 'react-native-vector-icons/FontAwesome';
import Header from '../../components/Header/Header';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import {styles} from './styles';
import CardsService from '../../services/CardsService/CardsService';
import {useUserStore} from '../../store/UserStore';
import IoPayService from '../../services/IoPayService/IoPayService';
import {Images} from '../../constants/Images';
import {extractErrorMessage} from '../../utils/extractErrorMessage';
import TemporaryPasswordBanner from '../../components/TemporaryPasswordBanner/TemporaryPasswordBanner';

const MyCards = () => {
  const {user} = useUserStore();
  const temporaryPasswordLogin = useUserStore(state => state.temporaryPasswordLogin);
  const {navigate, goBack} = useNavigation();
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);

  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useFocusEffect(
    useCallback(() => {
      const fetchCards = async () => {
        setLoading(true);
        try {
          console.log("Fetching customer data...");
          const customer = await CardsService.GetCustomer(user?.clientCode);

          if (!customer?.customer) {
            console.log("No customer data found.");
            setCards([]);
            return;
          }
          console.log("Customer data fetched successfully:", customer);

          const token = await authenticate();

          const cardsResponse = await CardsService.GetCards(user?.clientCode, token);
          const cards = cardsResponse.cards;

          if (cards?.items?.length) {
            const cardImages: {[key: string]: any} = {
              Visa: Images.visaIcon,
              MasterCard: Images.masterCardIcon,
            };

            const mappedCards = cards.items.map((card: any) => ({
              id: card.id_card,
              brand: card.card_brand,
              type: 'Crédito',
              lastDigits: card.last4_digits,
              isFavorite: cards.default_debit?.id_card === card.id_card,
              image: cardImages[card.card_brand] || '',
            }));

            setCards(mappedCards);
          } else {
            setCards([]);
          }
        } catch (error) {
          console.error('Erro ao buscar cartões:', error);
          Alert.alert('Erro ao buscar cartões!', extractErrorMessage(error));
        } finally {
          setLoading(false);
        }
      };

      fetchCards();
    }, [user?.clientCode]),
  );

  const authenticate = async () => {
    const response = await IoPayService.IoPayAuth();
    return response.access_token;
  };

  const handleStarPress = (cardId: string) => {
    const selected = cards.find(c => c.id === cardId);
    if (selected?.isFavorite) return;
    setSelectedCardId(cardId);
    setShowFavoriteModal(true);
  };

  const handleConfirmFavorite = async () => {
    try {
      setLoading(true);
      const token = await authenticate();
      const response = await CardsService.SetDefault(
        user?.clientCode,
        selectedCardId,
        token,
      );

      setCards(prev =>
        prev.map(card => ({
          ...card,
          isFavorite: card.id === selectedCardId,
        })),
      );
    } catch (error) {
      Alert.alert('Erro ao favoritar cartão!', extractErrorMessage(error));
    }
    setLoading(false);
    setShowFavoriteModal(false);
  };

  const handleDeletePress = (cardId: string) => {
    setSelectedCardId(cardId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      const token = await authenticate();
      const response = await CardsService.RemoveCard(
        user?.clientCode,
        selectedCardId,
        token,
      );

      setCards(prev => prev.filter(card => card.id !== selectedCardId));
    } catch (error) {
      Alert.alert('Erro ao remover cartão!', extractErrorMessage(error));
    }
    setLoading(false);
    setShowDeleteModal(false);
  };

  const renderItem = ({item}: any) => (
    <View style={styles.card}>
      <TouchableOpacity
        onPress={() => handleStarPress(item.id)}
        disabled={temporaryPasswordLogin}
        style={{opacity: temporaryPasswordLogin ? 0.4 : 1}}>
        <FontAwesome
          name={item.isFavorite ? 'star' : 'star-o'}
          size={22}
          color={item.isFavorite ? '#FFD700' : '#aaa'}
          style={{marginRight: 12}}
        />
      </TouchableOpacity>

      {item.image && <Image source={item.image} style={styles.cardImage} />}
      <View style={styles.cardInfo}>
        <Text style={styles.cardTitle}>{`${item.brand} - ${item.type}`}</Text>
        <Text style={styles.cardNumber}>**** {item.lastDigits}</Text>
      </View>
      <TouchableOpacity
        onPress={() => handleDeletePress(item.id)}
        disabled={temporaryPasswordLogin}
        style={[
          styles.deleteButton,
          {opacity: temporaryPasswordLogin ? 0.4 : 1},
        ]}>
        <FontAwesome name="trash" size={20} color={Colors.RedPallete.red} />
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.container}>
      <Header />
      <View style={styles.spaceHeader}>
        <SearchHeader onPress={goBack} title="Meus Cartões" mb={20} />
      </View>
      <TemporaryPasswordBanner />

      {loading && (
        <View style={{padding: 16}}>
          <ActivityIndicator size="large" color="#000" />
        </View>
      )}

      {!loading && cards.length === 0 && (
        <View style={{padding: 16, alignItems: 'center'}}>
          <Text style={{fontSize: 16, color: '#555'}}>
            Você ainda não tem cartões cadastrados
          </Text>
        </View>
      )}

      <FlatList
        data={cards}
        renderItem={renderItem}
        keyExtractor={item => item.id}
        contentContainerStyle={styles.listContent}
      />

      <TouchableOpacity
        style={[
          styles.addButton,
          temporaryPasswordLogin && {opacity: 0.4},
        ]}
        disabled={temporaryPasswordLogin}
        onPress={() => navigate('NewCard')}>
        <Text style={styles.addButtonText}>Adicionar novo cartão</Text>
      </TouchableOpacity>

      <Modal transparent visible={showFavoriteModal} animationType="fade">
        <View style={styles.modalBackground}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalText}>
              Deseja tornar este cartão o favorito?
            </Text>
            <View style={styles.modalActions}>
              <Pressable
                onPress={() => !loading && setShowFavoriteModal(false)}
                disabled={loading}
                style={({pressed}) => [
                  {opacity: loading ? 0.5 : pressed ? 0.7 : 1},
                ]}>
                <Text style={styles.modalCancel}>Cancelar</Text>
              </Pressable>
              <Pressable
                onPress={() => !loading && handleConfirmFavorite()}
                disabled={loading}
                style={({pressed}) => [
                  {opacity: loading ? 0.5 : pressed ? 0.7 : 1},
                ]}>
                <Text style={styles.modalConfirm}>Confirmar</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal transparent visible={showDeleteModal} animationType="fade">
        <View style={styles.modalBackground}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalText}>Deseja excluir este cartão?</Text>
            <View style={styles.modalActions}>
              <Pressable
                onPress={() => setShowDeleteModal(false)}
                style={({pressed}) => [
                  {opacity: loading ? 0.5 : pressed ? 0.7 : 1},
                ]}
                disabled={loading}>
                <Text style={styles.modalCancel}>Cancelar</Text>
              </Pressable>
              <Pressable
                onPress={handleConfirmDelete}
                disabled={loading}
                style={({pressed}) => [
                  {opacity: loading ? 0.5 : pressed ? 0.7 : 1},
                ]}>
                <Text style={styles.modalConfirm}>Excluir</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default MyCards;
